import React from "react";
import { api } from "../../../api";
import {
  Details,
  Service,
  Extras,
  Confirmation,
  Step,
  CustomProgressBar,
  BundleModal,
} from "../../../components/";
import "./update.css";
import * as Images from "../../../themes/images";
import { instanceOf } from "prop-types";
import { withCookies, Cookies } from "react-cookie";
import Loading from "../../../components/Loading";
import { withAuth0 } from "@auth0/auth0-react";
import { BookingTimeModal } from "../../../components/";
import moment from "moment";
import Button from "@mui/material/Button";
import HeaderMobile from "../../../components/headerMobile";
import ReactPixel from "react-facebook-pixel";
import { Helmet } from "react-helmet";

class UpdateBooking extends React.Component {
  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };
  constructor(props) {
    super(props);
    const { cookies } = props;
    this.state = {
      isLoading: true,
      booking: null,
      openBookingModal: false,
      locationAvailability: null,
      availibilityInterval: 15,
      selectedDate: null,
      selectedTime: null,
      checkModalOpen: false,
      selectedLocation: null,
    };
    this.baseState = this.state;

    this.getBooking = this.getBooking.bind(this);
    this.toggleDateModal = this.toggleDateModal.bind(this);
    this.updateSelectedDate = this.updateSelectedDate.bind(this);
    this.handleUpdateDateTime = this.handleUpdateDateTime.bind(this);
    this.handleSelectedTime = this.handleSelectedTime.bind(this);
    this.checkModal = this.checkModal.bind(this);
    this.cancelBooking = this.cancelBooking.bind(this);
    this.updateBookingDate = this.updateBookingDate.bind(this);
  }

  async getBooking(id) {
    try {
      // First try the new booking flow API
      let booking = null;
      let isNewBookingFlow = false;

      try {
        const newBookingResponse = await api(
          `booking-flow/booking/${id}`,
          "GET",
          null
        );
        if (
          newBookingResponse.status === 200 &&
          newBookingResponse.data.success
        ) {
          booking = newBookingResponse.data;
          isNewBookingFlow = true;
        }
      } catch (newBookingError) {
        // If new booking flow fails, try the old booking API
        console.log("New booking flow not found, trying old booking API");
      }

      // If new booking flow didn't work, try the old booking API
      if (!booking) {
        const oldBookingResponse = await api(`booking/${id}`, "GET", null);
        if (
          oldBookingResponse.status === 200 &&
          oldBookingResponse.data.success
        ) {
          booking = oldBookingResponse.data;
          isNewBookingFlow = false;
        }
      }

      if (booking) {
        // Normalize the booking data structure for both old and new formats
        const normalizedBooking = this.normalizeBookingData(
          booking,
          isNewBookingFlow
        );

        this.setState({
          booking: normalizedBooking,
          selectedLocation: normalizedBooking.idLocation, // Use idLocation for compatibility
          isLoading: false,
          isNewBookingFlow: isNewBookingFlow,
        });
      } else {
        alert("Could not find booking with booking reference provided.");
        this.setState({
          booking: null,
          isLoading: false,
        });
      }
    } catch (error) {
      console.log("error: ", error);
      alert("Could not find booking with booking reference provided.");
      this.setState({
        booking: null,
        isLoading: false,
      });
    }
  }

  // Normalize booking data structure for both old and new booking flows
  normalizeBookingData(bookingData, isNewBookingFlow) {
    if (isNewBookingFlow) {
      // New booking flow structure
      const booking = bookingData.booking || {};
      const cart = bookingData.cart || {};
      const location = bookingData.location || {};

      return {
        // Basic booking info
        _id: booking._id,
        posBookingKey: booking.posBookingKey,
        bookingKey: booking.posBookingKey, // Add bookingKey alias for compatibility
        status: booking.status,
        posResponse: booking.posResponse,
        date: cart.step1Data?.selectedDate || "",
        time: this.parseTimeFromTimeSlot(cart.step1Data?.selectedTimeSlot),

        // Location info (both idLocation and location for compatibility)
        idLocation: {
          _id: location._id,
          name: location.name,
          address: location.address,
          coordinates: cart.step1Data?.selectedLocation?.coordinates || [],
          city: cart.step1Data?.selectedLocation?.city || "",
          timezone: cart.step1Data?.selectedLocation?.timezone || "Australia/Sydney",
          seoName: location.seoName || location.name.toLowerCase().replace(/\s+/g, '-'), // Use actual seoName or generate fallback
        },
        location: {
          _id: location._id,
          name: location.name,
          address: location.address,
          coordinates: cart.step1Data.selectedLocation.coordinates,
          city: cart.step1Data.selectedLocation.city,
          timezone: cart.step1Data.selectedLocation.timezone,
          seoName: location.seoName || location.name.toLowerCase().replace(/\s+/g, '-'), // Use actual seoName or generate fallback
        },

        // Service info (make selectedServices an array for compatibility)
        selectedServices: [cart.step2Data.selectedBundle?.name || "Express Wash"],
        serviceDetails: cart.step2Data.selectedBundle,

        // User info
        userDetails: {
          firstName: cart.step3Data?.firstName || "",
          lastName: cart.step3Data?.lastName || "",
          phone: cart.step3Data?.phone || "",
          email: cart.step1Data.email,
          carRego: cart.step3Data?.carRego || "",
        },

        // Cart info for compatibility
        cart: {
          sessionData: {
            selectedLocation: {
              _id: location._id,
              name: location.name,
              address: location.address,
              coordinates: cart.step1Data.selectedLocation.coordinates,
              city: cart.step1Data.selectedLocation.city,
              timezone: cart.step1Data.selectedLocation.timezone,
              seoName: location.seoName || location.name.toLowerCase().replace(/\s+/g, '-'),
            },
            selectedServiceType:
              cart.step2Data.selectedBundle?.name || "Express Wash",
            bookingUserDetails: {
              firstName: cart.step3Data?.firstName || "",
              lastName: cart.step3Data?.lastName || "",
              phone: cart.step3Data?.phone || "",
              email: cart.step1Data.email,
              carRego: cart.step3Data?.carRego || "",
            },
          },
        },

        // Additional new booking flow data
        bookingFlowCartId: booking.bookingFlowCartId,
        stripePaymentIntentId: booking.stripePaymentIntentId,
        bookingFee: booking.bookingFee,
      };
    } else {
      // Old booking flow structure - return as is with some normalization
      return {
        ...bookingData.booking,
        location: bookingData.booking.idLocation,
      };
    }
  }

  // Parse time from new booking flow timeSlot format
  parseTimeFromTimeSlot(timeSlot) {
    if (!timeSlot) return { hour: 0, minute: 0, ampm: "AM" };

    // Extract time from timeRange like "12:15 PM - 12:30 PM"
    const timeRange = timeSlot.timeRange || timeSlot.timeKey;
    if (timeRange && timeRange.includes(":")) {
      const timeMatch = timeRange.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (timeMatch) {
        return {
          hour: parseInt(timeMatch[1]),
          minute: parseInt(timeMatch[2]),
          ampm: timeMatch[3].toUpperCase(),
        };
      }
    }

    // Fallback to timeKey like "12:15"
    if (timeSlot.timeKey) {
      const [hours, minutes] = timeSlot.timeKey.split(":");
      const hour = parseInt(hours);
      return {
        hour: hour > 12 ? hour - 12 : hour === 0 ? 12 : hour,
        minute: parseInt(minutes),
        ampm: hour >= 12 ? "PM" : "AM",
      };
    }

    return { hour: 0, minute: 0, ampm: "AM" };
  }

  toggleDateModal(val) {
    this.setState({
      openBookingModal: val,
    });
  }

  async updateSelectedDate(val) {
    let data = {
      date: val,
      selectedLocation: this.state.booking.idLocation._id,
    };

    let res = await api("location/availability", "POST", data);
    if (res.status === 200) {
      if (res.data.success) {
        let locationAvailability = res.data.locationAvailability;
        this.setState({
          locationAvailability: locationAvailability,
          availibilityInterval: res.data.interval,
          serviceSpecificCapacities: res.data.serviceSpecificCapacities,
        });
        // return res.data.locationAvailability;
      }
    } else {
      console.log(res);
    }
  }

  async handleUpdateDateTime() {
    // console.log("updateSelectedDate: ", this.state);
  }

  async handleSelectedTime(val, utc, am, range) {
    this.setState({
      selectedDate: val,
      selectedTime: range,
    });
  }

  async cancelBooking() {
    try {
      let res;

      if (this.state.isNewBookingFlow) {
        // Use new booking flow API
        res = await api(
          `booking-flow/booking/${this.state.booking.posBookingKey}/cancel`,
          "PUT",
          {
            bookingId: this.state.booking._id,
          }
        );
      } else {
        // Use old booking flow API
        res = await api(
          `booking/cancel/${this.state.booking.posBookingKey}`,
          "PUT",
          null
        );
      }

      if (res.status === 200) {
        if (res.data.success) {
          alert("Booking was canceled.");
          window.location.replace("/manage-booking");
        }
      } else {
        console.log(res);
        alert("Failed to cancel booking. Please try again.");
      }
    } catch (error) {
      console.error("Error canceling booking:", error);
      alert("Could not cancel booking. Please try again.");
      this.setState({
        booking: null,
        isLoading: false,
      });
    }
  }

  async updateBookingDate() {
    if (this.state.selectedDate == null) {
      alert("Please update date and time before saving.");
      return;
    }

    try {
      let res;

      if (this.state.isNewBookingFlow) {
        // Use new booking flow API
        res = await api(
          `booking-flow/booking/${this.state.booking.posBookingKey}/update`,
          "PUT",
          {
            selectedBookingTime: this.state.selectedDate,
            bookingId: this.state.booking._id,
          }
        );
      } else {
        // Use old booking flow API
        res = await api(
          `booking/update/${this.state.booking.bookingKey}`,
          "PUT",
          {
            selectedBookingTime: this.state.selectedDate,
          }
        );
      }

      if (res.status === 200) {
        if (res.data.success) {
          alert("Booking Date & Time updated.");
          window.location.reload();
        }
      } else {
        console.log(res);
        if (res.status == 404) {
          alert(res.data.response || res.data.message || "Booking not found");
        } else {
          alert("Failed to update booking. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      alert("Failed to update booking. Please try again.");
    }
  }

  checkModal(isOpen) {
    if (isOpen) {
      return (
        <div className="checkModalContainer">
          <div className="checkModalCard">
            <h3>Are you sure you want to cancel your booking?</h3>
            <div className="buttonSection">
              <Button
                onClick={() =>
                  this.setState({
                    checkModalOpen: !this.state.checkModalOpen,
                  })
                }
                className="blueBorderButton modalButton"
              >
                Keep Booking
              </Button>
              <Button
                onClick={() => {
                  this.setState({
                    checkModalOpen: !this.state.checkModalOpen,
                    isLoading: true,
                  });
                  this.cancelBooking();
                }}
                className="lightBlueButton modalButton"
              >
                Cancel Booking
              </Button>
            </div>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  }

  componentDidMount() {
    document.title = "Update Booking - Concierge Car Wash";
    let { cookies } = this.props;
    ReactPixel.pageView();
    const queryParams = new URLSearchParams(window.location.search);
    const hasBookingId = queryParams.get("bookingid");

    if (hasBookingId) {
      this.getBooking(hasBookingId);
    } else {
      this.setState({
        isLoading: false,
      });
    }
  }

  render() {
    var selectedDate = null;
    var Time = null;
    if (this.state.selectedDate) {
      selectedDate = this.state.selectedDate;
      Time = this.state.selectedTime;
    } else {
      if (this.state.booking) {
        let date = moment(this.state.booking.date).format("MMM Do");
        let firstTime =
          this.state.booking.time.hour + this.state.booking.time.ampm;
        let secondTime =
          Number(this.state.booking.time.hour) + 1 >= 12
            ? Number(this.state.booking.time.hour) + 1 + "PM"
            : Number(this.state.booking.time.hour) + 1 + "AM";
        let range = firstTime + " - " + secondTime;
        Time = range;
        selectedDate = `${date} ${range}`;
      }
    }
    const meta = {
      title: "Amend your booking with Concierge Car Wash",
      description:
        "Update, upgrade or reschedule your Concierge Car Wash booking using our new and fast booking system. Australia's premium 100% Hand-Only car wash destination.",
    };

    return (
      <div className="bookingPage manageBooking">
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
        </Helmet>
        {window.innerWidth < 767 && <HeaderMobile />}
        <div className="bookingBanner" style={{ height: 50 }}></div>
        <div className="bookingHeader manageBookingHeader">
          <div className="logoSection">
            <a href="/">
              <img src={Images.ccwLogoLight} className="ccwLogo" alt="Logo" />
            </a>
          </div>
          <div className="titleSection manageBookingTitle">
            <h1>Manage Your Booking</h1>
          </div>
          <div className="closeButtonSection"></div>
        </div>
        {this.state.isLoading ? (
          <Loading />
        ) : (
          <>
            <div className="updateBookingContainer">
              <div className="flexCol headings">
                <div
                  className={
                    this.state.booking && window.innerWidth < 767
                      ? "updateCell updateCellLeft"
                      : "updateCell"
                  }
                >
                  <h5>Booking Reference</h5>
                </div>
                {this.state.booking && window.innerWidth > 767 && (
                  <div>
                    <div className="updateCell">
                      <h5>Date and Arrival Time</h5>
                    </div>
                    <div className="updateCell">
                      <h5>Location</h5>
                    </div>
                    <div className="updateCell">
                      <h5>Service</h5>
                    </div>
                    <div className="updateCell">
                      <Button
                        onClick={() => this.updateBookingDate()}
                        className="lightBlueButton"
                      >
                        Save Date & Time
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flexCol inputs">
                <div
                  className={
                    this.state.booking && window.innerWidth < 767
                      ? "updateCell updateCellLeft"
                      : "updateCell"
                  }
                >
                  <input
                    name="bookingRef"
                    id="bookingRef"
                    defaultValue={
                      this.state.booking ? this.state.booking.posBookingKey : ""
                    }
                    onBlur={(val) => this.getBooking(val.target.value)}
                  />
                </div>
                {this.state.booking && (
                  <div className="updateBookingTimeWrapper">
                    {window.innerWidth < 767 && (
                      <div className="updateCellLeft">
                        <h5>Date and Arrival Time</h5>
                      </div>
                    )}
                    <div className="updateCell">
                      <Button
                        className="bookingTimeButton"
                        onClick={() =>
                          this.toggleDateModal(!this.state.openBookingModal)
                        }
                      >
                        <span>
                          {selectedDate
                            ? selectedDate
                            : "Select your booking date and time"}
                        </span>
                        <img
                          src={Images.calendarIco}
                          className="calendarIco"
                          alt="calendar"
                        />
                      </Button>
                    </div>
                    {window.innerWidth < 767 && (
                      <div className="updateCellLeft">
                        <h5>Location</h5>
                      </div>
                    )}
                    <div
                      className={
                        window.innerWidth < 767
                          ? "updateCell bookingTextMobile"
                          : "updateCell"
                      }
                    >
                      <p className="bookingText">
                        {this.state.booking &&
                          this.state.booking.idLocation.seoName}
                      </p>
                    </div>
                    {window.innerWidth < 767 && (
                      <div className="updateCellLeft">
                        <h5>Service</h5>
                      </div>
                    )}
                    <div
                      className={
                        window.innerWidth < 767
                          ? "updateCell bookingTextMobile"
                          : "updateCell"
                      }
                    >
                      <p className="bookingText">
                        {this.state.booking &&
                          this.state.booking.selectedServices.map(function (
                            item
                          ) {
                            return item;
                          })}
                      </p>
                    </div>
                    {window.innerWidth > 820 && (
                      <div className="updateCell">
                        <Button
                          onClick={() =>
                            this.setState({
                              checkModalOpen: !this.state.checkModalOpen,
                            })
                          }
                          className="blueBorderButton"
                        >
                          Cancel Booking
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {this.state.booking && window.innerWidth < 767 && (
                <div className="fixedButton">
                  <Button
                    onClick={() =>
                      this.setState({
                        checkModalOpen: !this.state.checkModalOpen,
                      })
                    }
                    className="blueBorderButton"
                  >
                    Cancel Booking
                  </Button>
                  <Button
                    onClick={() => this.updateBookingDate()}
                    className="lightBlueButton"
                  >
                    Save Date & Time
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
        {this.state.booking == null && (
          <div className="flexRow manageBookingButtons">
            <div className="searchButtonSection">
              <Button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="blueBorderButton"
              >
                Go Back
              </Button>
            </div>
            <div className="searchButtonSection">
              <Button onClick={null} className="lightBlueButton">
                Search
              </Button>
            </div>
          </div>
        )}
        <BookingTimeModal
          locationAvailability={this.state.locationAvailability}
          availibilityInterval={this.state.availibilityInterval}
          openBookingModal={this.state.openBookingModal}
          selectedBookingTime={selectedDate}
          toggleDateModal={this.toggleDateModal}
          selectedDate={selectedDate}
          selectedTime={Time}
          updateSelectedDate={this.updateSelectedDate}
          handleSelectedTime={this.handleSelectedTime}
          updateBooking={true}
          updateSelectedService={this.handleUpdateDateTime}
          location={this.state.selectedLocation}
        />
        {this.checkModal(this.state.checkModalOpen)}
      </div>
    );
  }
}

export default withCookies(UpdateBooking);
